import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useAuth } from './useAuth';

// Must match supabase/config.toml: minimum_password_length and password_requirements.
const MIN_LENGTH = 10;
const REQUIREMENTS = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/;

export default function UpdatePasswordPage() {
  const { t } = useTranslation();
  const { updatePassword } = useAuth();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    if (password.length < MIN_LENGTH || !REQUIREMENTS.test(password)) {
      setError(t('auth.passwordTooWeak', { length: MIN_LENGTH }));
      return;
    }
    if (password !== confirm) {
      setError(t('auth.passwordMismatch'));
      return;
    }

    setSubmitting(true);
    try {
      await updatePassword(password);
      void navigate('/', { replace: true });
    } catch {
      setError(t('common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="flex min-h-dvh items-center justify-center p-6">
      <form
        onSubmit={(e) => void onSubmit(e)}
        className="flex w-full max-w-sm flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
      >
        <h1 className="text-base font-semibold text-slate-900">{t('auth.updatePasswordTitle')}</h1>

        {error ? <Alert tone="error">{error}</Alert> : null}

        <Input
          label={t('auth.newPassword')}
          type="password"
          autoComplete="new-password"
          required
          dir="ltr"
          hint={t('auth.passwordHint', { length: MIN_LENGTH })}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />

        <Input
          label={t('auth.confirmPassword')}
          type="password"
          autoComplete="new-password"
          required
          dir="ltr"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />

        <Button type="submit" loading={submitting}>
          {t('common.save')}
        </Button>
      </form>
    </main>
  );
}
